import React, { useEffect, useId, useRef, useState } from 'react';
import './TesseractAnimation.css';

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const hexToRgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
};

const shadeHex = (hex, t) => {
  const [r, g, b] = hexToRgb(hex);
  const k = 1 - clamp01(t) * 0.28;
  return `rgb(${Math.round(r * k)},${Math.round(g * k)},${Math.round(b * k)})`;
};

const depthTone = (z, w) => clamp01(0.5 + z * 0.4 + w * 0.18);

const TesseractAnimation = ({
  size = 80,
  color = '#ffffff',
  strokeWidth = 1,
  speed = 1,
  shadeDepth = false,
}) => {
  const [lines, setLines] = useState([]);
  const animationRef = useRef(null);
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const stateRef = useRef({
    angleXY: 0,
    angleXZ: 0,
    angleXW: 0,
    angleYZ: 0,
    angleYW: 0,
    angleZW: 0,
    unfoldProgress: 0,
    unfoldStartTime: null,
  });

  // Define tesseract vertices in 4D space
  const vertices4D = [];
  for (let i = 0; i < 16; i++) {
    const x = (i & 1) ? 1 : -1;
    const y = (i & 2) ? 1 : -1;
    const z = (i & 4) ? 1 : -1;
    const w = (i & 8) ? 1 : -1;
    vertices4D.push([x, y, z, w]);
  }

  // Define edges (connect vertices that differ by exactly 1 coordinate)
  const edges = [];
  for (let i = 0; i < 16; i++) {
    for (let j = i + 1; j < 16; j++) {
      let diff = 0;
      for (let k = 0; k < 4; k++) {
        if (vertices4D[i][k] !== vertices4D[j][k]) diff++;
      }
      if (diff === 1) {
        edges.push([i, j]);
      }
    }
  }

  const rotate4D = (x, y, z, w, state) => {
    // XY plane rotation
    if (state.angleXY !== 0) {
      const cosXY = Math.cos(state.angleXY);
      const sinXY = Math.sin(state.angleXY);
      const xNew = x * cosXY - y * sinXY;
      const yNew = x * sinXY + y * cosXY;
      x = xNew;
      y = yNew;
    }

    // YZ plane rotation
    if (state.angleYZ !== 0) {
      const cosYZ = Math.cos(state.angleYZ);
      const sinYZ = Math.sin(state.angleYZ);
      const yNew = y * cosYZ - z * sinYZ;
      const zNew = y * sinYZ + z * cosYZ;
      y = yNew;
      z = zNew;
    }

    // XW plane rotation (creates the cell rotation effect!)
    if (state.angleXW !== 0) {
      const cosXW = Math.cos(state.angleXW);
      const sinXW = Math.sin(state.angleXW);
      const xNew = x * cosXW - w * sinXW;
      const wNew = x * sinXW + w * cosXW;
      x = xNew;
      w = wNew;
    }

    return [x, y, z, w];
  };

  const project4DTo3D = (x, y, z, w) => {
    const distance = 2.5;
    let wOffset = distance - w;
    if (Math.abs(wOffset) < 0.1) wOffset = 0.1;

    const factor = distance / wOffset;

    return [x * factor, y * factor, z * factor];
  };

  const project3DTo2D = (x, y, z, unfoldProgress, canvasSize) => {
    // Apply unfold scale
    x *= unfoldProgress;
    y *= unfoldProgress;
    z *= unfoldProgress;

    // Perspective projection
    const distance = 5;
    const factor = distance / (distance + z);

    const scale = canvasSize / 9;
    const screenX = canvasSize / 2 + x * factor * scale;
    const screenY = canvasSize / 2 + y * factor * scale;

    return [screenX, screenY, z];
  };

  const animate = () => {
    const state = stateRef.current;

    // Update unfold progress
    if (state.unfoldProgress < 1.0) {
      if (!state.unfoldStartTime) {
        state.unfoldStartTime = Date.now();
      }
      const elapsed = Date.now() - state.unfoldStartTime;
      state.unfoldProgress = Math.min(1.0, elapsed / 200); // 200ms unfold
    }

    const pace = speedRef.current;
    state.angleXW += 0.008 * pace;
    state.angleYZ += 0.006 * pace;
    state.angleXY += 0.004 * pace;

    // Transform all vertices from 4D to 2D
    const vertices2D = vertices4D.map(vertex => {
      let [x, y, z, w] = vertex;

      // Apply 4D rotation
      [x, y, z, w] = rotate4D(x, y, z, w, state);

      // Project 4D -> 3D -> 2D
      const [x3, y3, z3] = project4DTo3D(x, y, z, w);
      const [screenX, screenY, depth] = project3DTo2D(x3, y3, z3, state.unfoldProgress, size);

      return [screenX, screenY, depth, w];
    });

    // Sort edges by average depth for proper rendering order
    const edgeDepths = edges.map(([i, j]) => {
      const avgDepth = (vertices2D[i][2] + vertices2D[j][2]) / 2;
      const avgW = (vertices2D[i][3] + vertices2D[j][3]) / 2;
      return [avgDepth + avgW, i, j];
    });

    edgeDepths.sort((a, b) => a[0] - b[0]);

    // Build lines array for SVG rendering
    const newLines = [];
    edgeDepths.forEach(([depthKey, i, j]) => {
      const [x1, y1, z1, w1] = vertices2D[i];
      const [x2, y2, z2, w2] = vertices2D[j];

      // Skip edges that are behind the camera or too far
      if (w1 < -2 || w2 < -2 || w1 > 2 || w2 > 2) return;

      // Skip edges that are way outside the visible area
      const margin = 10;
      if ((x1 < -margin && x2 < -margin) || (x1 > size + margin && x2 > size + margin)) return;
      if ((y1 < -margin && y2 < -margin) || (y1 > size + margin && y2 > size + margin)) return;

      newLines.push({
        x1,
        y1,
        x2,
        y2,
        t1: depthTone(z1, w1),
        t2: depthTone(z2, w2),
      });
    });

    setLines(newLines);
    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    // Start animation
    stateRef.current.unfoldStartTime = Date.now();
    stateRef.current.unfoldProgress = 0;
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  const uid = useId().replace(/:/g, '');

  return (
    <svg
      width={size}
      height={size}
      className="tesseract-animation"
      style={{ width: size, height: size }}
    >
      {shadeDepth && (
        <defs>
          {lines.map((line, index) => (
            <linearGradient
              key={index}
              id={`${uid}-${index}`}
              gradientUnits="userSpaceOnUse"
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
            >
              <stop offset="0%" stopColor={shadeHex(color, line.t1)} />
              <stop offset="100%" stopColor={shadeHex(color, line.t2)} />
            </linearGradient>
          ))}
        </defs>
      )}
      {lines.map((line, index) => (
        <line
          key={index}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={shadeDepth ? `url(#${uid}-${index})` : color}
          strokeWidth={strokeWidth}
          opacity={shadeDepth ? 0.78 + (1 - (line.t1 + line.t2) / 2) * 0.22 : 1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
};

export default TesseractAnimation;
